import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import CreatorStart from "@/pages/CreatorStart";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import FanPortal from "@/pages/FanPortal";
import FamilyView from "@/pages/FamilyView";
import JoinFamily from "@/pages/JoinFamily";
import SiloManage from "@/pages/SiloManage";
import SuperAdmin from "@/pages/SuperAdmin";
import AccountSettings from "@/pages/AccountSettings";
import FanSettings from "@/pages/FanSettings";
import PublicSilo from "@/pages/PublicSilo";
import SiloAssistant from "@/pages/SiloAssistant";
import CreatorNod from "@/pages/CreatorNod";
import TerminationPolicy from "@/pages/TerminationPolicy";
import MagicLinkInvite from "@/pages/MagicLinkInvite";
import CreatorProfile from "@/pages/CreatorProfile";
import Discover from "@/pages/Discover";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import FamilyPicker from "@/pages/FamilyPicker";
import KidLanding from "@/pages/KidLanding";
import KidHome from "@/pages/KidHome";
import MasterDashboard from "@/pages/MasterDashboard";
import VoucherEmptyListener from "@/components/VoucherEmptyListener";
import LLMGuardModal from "@/components/LLMGuardModal";
// Silo Wars (add-on, isolated). Remove these 4 imports + the 4 routes
// below to fully unwire the feature on the frontend.
import BattlesHub from "@/features/battles/BattlesHub";
import Arena from "@/features/battles/Arena";
import InviteRedeemPage from "@/features/battles/InviteRedeemPage";
import HallOfWars from "@/features/battles/HallOfWars";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import HelpReportButton from "@/components/HelpReportButton";
import { api, getToken, getUser, saveAuth } from "@/lib/api";
import { hasPlatformAccess } from "@/lib/access";
import PaymentReconciler from "@/components/PaymentReconciler";
import InstallIconSheet from "@/components/InstallIconSheet";
import CreatorProGateProvider from "@/components/CreatorProGateProvider";
import TrialSurfaces from "@/components/TrialSurfaces";
import { SiloArtProvider } from "@/components/SiloArtLayer";
import { getVisitorSig } from "@/lib/attribution";

const Protected = ({ children, superOnly = false }) => {
  const token = getToken();
  const user = getUser();
  if (!token || !user) return <Navigate to="/login" replace />;
  if (superOnly && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
};

// Oct 2026 (Randy pt-launch-target) — standalone session restore, ABOVE the
// route guards.
//
// A home-screen icon can now open an authed destination (AI Assistant, Chats,
// YTRE). iOS partitions web storage for a standalone PWA, so a freshly
// launched icon has no sessionStorage token — and `Protected` above redirects
// to /login SYNCHRONOUSLY on first render, before any page-level effect could
// restore the session. The Public Q&A never hit this because it's unguarded.
//
// So the restore has to happen before <Routes> renders at all. Two mechanisms,
// both already on the backend: the single-use `?handoff=` token baked into the
// manifest's start_url (first launch only), then `/auth/session-from-cookie`,
// which works on EVERY launch because iOS shares cookies with standalone PWAs
// even though it partitions storage.
//
// Engages ONLY for a standalone launch without a token, or an explicit
// ?handoff=. A normal browser tab short-circuits in the useState initialiser
// and renders synchronously, so nothing changes for ordinary traffic.
//
// MODULE SCOPE, deliberately: the handoff token is SINGLE-USE on the server, so
// it may be presented exactly once per page load. React StrictMode runs this
// effect twice in development, which fired two concurrent exchanges — the
// atomic claim let one win and the other came back 400, and if the winner's
// response was the discarded one the token was burnt for nothing. A ref is
// reset by the StrictMode re-run; a module-scope promise is not. Both runs
// await the SAME restore, so one page load is one attempt either way.
let bootPromise = null;

const restoreSession = async () => {
  const params = new URLSearchParams(window.location.search);
  const handoff = params.get("handoff");
  if (handoff) {
    try {
      const r = await api.post("/auth/exchange-handoff", { handoff });
      if (r.data?.token && r.data?.user) saveAuth(r.data.token, r.data.user);
    } catch { /* expired or already redeemed — fall through to the cookie */ }
    // Single-use: strip it so a reload doesn't try to redeem it again.
    try {
      params.delete("handoff");
      const qs = params.toString();
      window.history.replaceState(
        {}, "",
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
      );
    } catch { /* ignore */ }
  }
  if (!getToken()) {
    try {
      const r = await api.post("/auth/session-from-cookie", {});
      if (r.data?.token && r.data?.user) saveAuth(r.data.token, r.data.user);
    } catch { /* no/invalid cookie — continue anonymous */ }
  }
};

const StandaloneSessionGate = ({ children }) => {
  const [ready, setReady] = useState(() => {
    try {
      const hasHandoff = new URLSearchParams(window.location.search).has("handoff");
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
      return !(hasHandoff || (standalone && !getToken()));
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (ready) return;
    if (!bootPromise) bootPromise = restoreSession();
    bootPromise.then(() => setReady(true));
  }, [ready]);

  if (!ready) {
    return (
      <div
        className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68] text-sm"
        data-testid="standalone-session-gate"
      >
        Opening your silo…
      </div>
    );
  }
  return children;
};

// iter-32: Route gate that sends non-paid fans to the FanPortal instead of
// the full Creator Dashboard. Super admins, active subscribers, comped
// accounts and (Oct 2026) live-trial users continue to see the Dashboard.
// Reads `has_platform_access` from the /auth/me payload via one shared
// helper — see lib/access.js for why that matters.
const DashboardGate = () => {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (hasPlatformAccess(user)) return <Dashboard />;
  // Oct 2026 (Randy, fan-first pass) — intent-aware. Somebody who came
  // through "Create a silo" and hasn't been past the start step yet is sent
  // there, not into a social feed full of other people's silos. Routing only;
  // /start stamps the ack on any choice (including "look around first"), so
  // this diverts exactly once.
  if (user.signup_intent === "creator" && !user.creator_start_ack_at) {
    return <Navigate to="/start" replace />;
  }
  return <FanPortal />;
};

// Oct 27, 2026 (Randy) — *"this page should not be accessable to fans or non
// creators. they should only be able to access the fan settings."* The
// advanced account + billing page is creator ground: platform key, YouTube
// connection, vouchers, payouts. A fan who backs out of Stripe, or types the
// URL, gets the lean Fan Settings page instead.
//
// Three ways in stay open: anybody with platform access right now, anybody who
// ever held a platform subscription (a resting creator still needs their own
// billing), and the post-checkout return itself — `?stripe=` — because the
// webhook can land a second after the browser does, and a freshly paid creator
// must never be bounced out of their own confirmation.
const AccountSettingsGate = () => {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  const returning = new URLSearchParams(window.location.search).has("stripe");
  // "inactive" is the DEFAULT a plain fan carries, so it cannot count as
  // history — only a real past state (canceled / cancelled / past_due /
  // trialing / active) means this person has billing of their own.
  const status = String(user.platform_subscription_status || "").toLowerCase();
  const everSubscribed = !!status && !["inactive", "none"].includes(status);
  if (hasPlatformAccess(user) || everSubscribed || returning) {
    return <AccountSettings />;
  }
  return <Navigate to="/fan/settings" replace />;
};

// Non-paid fans landing on /dashboard/silo/:siloId get redirected to /dashboard
// (which then renders the FanPortal). Paid creators get the full SiloManage.
const SiloManageGate = () => {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (hasPlatformAccess(user)) return <SiloManage />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  // P0 attribution hardening (Feb 7, 2026) — once per browser-session, link
  // any visitor_sig cookie to the logged-in user record. The Stripe upgrade
  // webhook later uses this list as a fallback when a user upgrades to paid
  // creator and there's no `awaiting_signup` row keyed to them (i.e. they
  // were already registered when their inviter shared the link). Idempotent
  // server-side; we only fire it once per page load to keep noise down.
  useEffect(() => {
    try {
      if (!getToken() || !getUser()) return;
      const sig = getVisitorSig();
      if (!sig) return;
      // Guard: only fire once per (user, sig) pair per page load.
      const key = `mysilo_sig_bound_${getUser().id}_${sig}`;
      if (sessionStorage.getItem(key)) return;
      api.post("/referrals/bind-visitor-sig", { visitor_sig: sig })
        .then(() => { try { sessionStorage.setItem(key, "1"); } catch { /* */ } })
        .catch(() => {});
    } catch { /* attribution wiring is best-effort */ }
  }, []);

  // Magic-link auto-redeem — if the user just signed up after clicking
  // an /invite/<token> email, the token sits in localStorage. Redeem
  // it once so attribution sticks across the device hop.
  useEffect(() => {
    try {
      if (!getToken() || !getUser()) return;
      const token = localStorage.getItem("magic_link_pending_invite_token");
      if (!token) return;
      api.post(`/invites/${token}/redeem`, {})
        .then(() => { try { localStorage.removeItem("magic_link_pending_invite_token"); } catch { /* */ } })
        .catch(() => { /* token may be expired/invalid — silent */ });
    } catch { /* best-effort */ }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        {/* The session gate is the OUTERMOST thing inside the router, on
            purpose. Mounted any deeper, the providers below it fire their own
            authed requests while the gate is still restoring; the anonymous
            401 then lands AFTER saveAuth, which makes api.js's interceptor
            read it as an expired session — it wipes the token, kills the
            mysilo_session cookie and redirects to /login. A launched icon
            ended up on the login screen with a valid session in hand. */}
        <StandaloneSessionGate>
        <SiloArtProvider>
        <CreatorProGateProvider>
        <ImpersonationBanner />
        <PaymentReconciler />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* Dynamic per-creator marketing page. Reads :refCode (their
              short referral code) + :siloSlug so the "See a live silo"
              demo button on the marketing page lands on THIS silo
              instead of Cosmic Equivalence. */}
          <Route path="/m/:refCode/:siloSlug" element={<Landing />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/start" element={<Protected><CreatorStart /></Protected>} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><DashboardGate /></Protected>} />
          {/* iter-35e: explicit fan-portal route. Lets creators preview the
              fan view of THEIR own profile (every silo they're subscribed to,
              their bookmarks, tips). Same component non-paying fans see at
              /dashboard. */}
          <Route path="/fan-portal" element={<Protected><FanPortal /></Protected>} />
          {/* Phase A (Jul 2026, Randy) — Family Access Point. The only door
              into a personal silo for FREE family members: capture pad +
              calendar, no creator dashboard. NOT behind SiloManageGate. */}
          <Route path="/family-view/:siloId" element={<Protected><FamilyView /></Protected>} />
          {/* Shareable family invite link — public landing (auth prompted on join). */}
          <Route path="/join-family/:token" element={<JoinFamily />} />
          <Route path="/dashboard/account-settings" element={<Protected><AccountSettingsGate /></Protected>} />
          <Route path="/fan/settings" element={<Protected><FanSettings /></Protected>} />
          <Route path="/dashboard/silo/:siloId" element={<Protected><SiloManageGate /></Protected>} />
          {/* Phase 3 — private AI Assistant page (the cube's left face). Owner-only. */}
          <Route path="/silo/:slug/assistant" element={<Protected><SiloAssistant /></Protected>} />
          <Route path="/admin" element={<Protected superOnly><SuperAdmin /></Protected>} />
          <Route path="/s/:slug" element={<PublicSilo />} />          {/* Phase-A — operator handshake landing. Gated client-side: only
              creators / affiliate-creators see it; fans get redirected to
              /signup with the seed preserved. */}
          <Route path="/creator-nod" element={<Protected><CreatorNod /></Protected>} />
          {/* Silo Wars — public spectator routes; arena handles its own gating. */}
          <Route path="/wars" element={<BattlesHub />} />
          <Route path="/wars/arena/:battleId" element={<Arena />} />
          <Route path="/wars/invite/:token" element={<InviteRedeemPage />} />
          <Route path="/wars/hall" element={<HallOfWars />} />
          <Route path="/policy/account-termination" element={<TerminationPolicy />} />
          {/* Magic-link invitations — cross-device attribution */}
          <Route path="/invite/:token" element={<MagicLinkInvite />} />
          {/* Creator Profile + Tag Discovery (Feb 13, 2026) */}
          <Route path="/u/:code" element={<CreatorProfile />} />
          <Route path="/discover" element={<Discover />} />
          {/* Jun 2026 — live legal pages + a friendly /account alias */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          {/* Jul 2026 (Randy) — kid profiles: family picker + magic-link entry + kid mode. */}
          <Route path="/family/:siloId" element={<Protected><FamilyPicker /></Protected>} />
          <Route path="/kid/:token" element={<KidLanding />} />
          <Route path="/kid-home" element={<KidHome />} />
          {/* Feb 2026 (Randy) — Master Dashboard: opt-in full-screen wall
              tablet experience for owner + parent-role members. */}
          <Route path="/master-dashboard/:siloId" element={<Protected><MasterDashboard /></Protected>} />
          <Route path="/account" element={<Navigate to="/dashboard/account-settings" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallIconSheet />
        {/* Feb 28, 2026 — LLMGuardModal uses useNavigate() so it MUST live
            inside <BrowserRouter>. Mounting it outside threw an uncaught
            runtime error on every page load. */}
        <LLMGuardModal />
        {/* Oct 2026 (TRIAL_PLAN.md §8) — every free-trial popup, one mount. */}
        <TrialSurfaces />
        <HelpReportButton />
        </CreatorProGateProvider>
        </SiloArtProvider>
        </StandaloneSessionGate>
      </BrowserRouter>
      <Toaster position="top-center" richColors expand visibleToasts={5} closeButton duration={6000} />
      {/* Feb 20, 2026 — Global voucher-empty listener. When any platform-key
          call returns 402 SILO_VOUCHER_EMPTY, the api.js interceptor
          dispatches a window event and this component shows a toast +
          can open the top-up modal directly. */}
      <VoucherEmptyListener />
    </div>
  );
}

export default App;
